import { PageLayout } from '@/components/layout'
import { Card, LoadingSpinner, ProgressBar } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { useUsage } from '@/hooks/useUsage'

interface ServiceUsageSummary {
  serviceName: string
  totalUsed: number
  totalLimit: number | null
  percentage: number
  periodEnd: string | null
  metrics: Array<{
    name: string
    used: number
    limit: number | null
    percentage: number
  }>
}

export const UsageTracking = () => {
  const { user } = useAuth()
  const { data: usageData, isLoading, error } = useUsage(user?.organizationId || '')

  // Group usage by service and calculate totals
  const serviceSummaries: ServiceUsageSummary[] =
    usageData?.services.map((service) => {
      let totalUsed = 0
      let totalLimit = 0
      let hasUnlimited = false
      const metrics = service.metrics.map((metric) => {
        const used = metric.count
        const limit = metric.limit && metric.limit !== -1 ? metric.limit : null
        const percentage = limit ? Math.min((used / limit) * 100, 100) : 0

        totalUsed += used
        if (limit) {
          totalLimit += limit
        } else {
          hasUnlimited = true
        }

        return {
          name: metric.metric_name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          used,
          limit,
          percentage,
        }
      })

      const overallPercentage = hasUnlimited
        ? 0
        : totalLimit > 0
          ? Math.min((totalUsed / totalLimit) * 100, 100)
          : 0

      return {
        serviceName: service.service_name,
        totalUsed,
        totalLimit: hasUnlimited ? null : totalLimit,
        percentage: overallPercentage,
        periodEnd: service.metrics[0]?.period_end || null,
        metrics,
      }
    }) || []

  const totalUsed = serviceSummaries.reduce((sum, service) => sum + service.totalUsed, 0)
  const totalLimit = serviceSummaries.reduce((sum, service) => {
    if (service.totalLimit === null) return sum
    return sum + service.totalLimit
  }, 0)
  const overallPercentage = totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0

  const getUsageColor = (percentage: number): 'primary' | 'success' | 'warning' | 'danger' => {
    if (percentage >= 90) return 'danger'
    if (percentage >= 75) return 'warning'
    if (percentage > 0) return 'success'
    return 'primary'
  }

  if (isLoading) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10 flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">
                Error loading usage data: {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout showHeader={true}>
      <div className="p-6 md:p-8 lg:p-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-main-light dark:text-text-main-dark mb-2">
              Usage Tracking
            </h1>
            <p className="text-text-sub-light dark:text-text-sub-dark">
              Monitor your service usage and track limits across all subscriptions
            </p>
          </div>

          {/* Overall Usage Summary */}
          <Card className="mb-8 p-6 bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10">
            <div className="mb-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-main-light dark:text-text-main-dark mb-2">
                    Overall Usage
                  </h2>
                  <p className="text-3xl font-bold text-text-main-light dark:text-text-main-dark">
                    {totalUsed.toLocaleString()} / {totalLimit > 0 ? totalLimit.toLocaleString() : 'Unlimited'}
                  </p>
                  {totalLimit > 0 && (
                    <p className="text-sm text-text-sub-light dark:text-text-sub-dark mt-1">
                      {overallPercentage.toFixed(1)}% of total limit used
                    </p>
                  )}
                </div>
                <div className="text-center md:text-right">
                  <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">
                    Next Reset
                  </p>
                  <p className="text-lg font-semibold text-text-main-light dark:text-text-main-dark">
                    {serviceSummaries[0]?.periodEnd
                      ? new Date(serviceSummaries[0].periodEnd).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>
              {totalLimit > 0 && (
                <ProgressBar
                  value={totalUsed}
                  max={totalLimit}
                  color={getUsageColor(overallPercentage)}
                  showLabel={false}
                />
              )}
            </div>
          </Card>

          {/* Service Usage Progress Bars */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
              Service Usage
            </h2>
            {serviceSummaries.length === 0 ? (
              <Card className="text-center py-12">
                <p className="text-text-sub-light dark:text-text-sub-dark">
                  No usage data available
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {serviceSummaries.map((service) => (
                  <Card key={service.serviceName} className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-bold text-text-main-light dark:text-text-main-dark">
                          {service.serviceName}
                        </h3>
                        <div className="text-right">
                          <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                            {service.totalUsed.toLocaleString()}{' '}
                            {service.totalLimit !== null
                              ? `/ ${service.totalLimit.toLocaleString()}`
                              : '/ Unlimited'}
                          </p>
                          {service.totalLimit !== null && (
                            <p className="text-xs text-text-sub-light dark:text-text-sub-dark">
                              {service.percentage.toFixed(1)}% used
                            </p>
                          )}
                        </div>
                      </div>
                      {service.totalLimit !== null ? (
                        <ProgressBar
                          value={service.totalUsed}
                          max={service.totalLimit}
                          color={getUsageColor(service.percentage)}
                          showLabel={false}
                          className="mb-2"
                        />
                      ) : (
                        <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-primary w-full rounded-full"></div>
                        </div>
                      )}
                      {service.periodEnd && (
                        <p className="text-xs text-text-sub-light dark:text-text-sub-dark mt-2">
                          Resets on{' '}
                          {new Date(service.periodEnd).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      )}
                    </div>

                    {/* Individual Metrics */}
                    {service.metrics.length > 1 && (
                      <div className="pt-4 border-t border-border-light dark:border-border-dark">
                        <h4 className="text-sm font-semibold text-text-main-light dark:text-text-main-dark mb-3">
                          Metrics Breakdown
                        </h4>
                        <div className="space-y-3">
                          {service.metrics.map((metric, index) => (
                            <div key={index}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-text-sub-light dark:text-text-sub-dark">
                                  {metric.name}
                                </span>
                                <span className="text-sm font-medium text-text-main-light dark:text-text-main-dark">
                                  {metric.used.toLocaleString()}{' '}
                                  {metric.limit !== null
                                    ? `/ ${metric.limit.toLocaleString()}`
                                    : '/ Unlimited'}
                                </span>
                              </div>
                              {metric.limit !== null ? (
                                <ProgressBar
                                  value={metric.used}
                                  max={metric.limit}
                                  color={getUsageColor(metric.percentage)}
                                  showLabel={false}
                                  className="h-1.5"
                                />
                              ) : (
                                <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className="h-full bg-primary w-full rounded-full"></div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Usage Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
              About Usage Tracking
            </h3>
            <div className="space-y-3 text-sm text-text-sub-light dark:text-text-sub-dark">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: '20px' }}>
                  info
                </span>
                <div>
                  <p className="font-semibold text-text-main-light dark:text-text-main-dark mb-1">
                    Monthly Reset
                  </p>
                  <p>
                    Usage counters reset automatically on your subscription renewal date each month.
                    Unused usage does not carry over to the next period.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: '20px' }}>
                  warning
                </span>
                <div>
                  <p className="font-semibold text-text-main-light dark:text-text-main-dark mb-1">
                    Limit Warnings
                  </p>
                  <p>
                    You'll receive warnings when you reach 75% and 90% of your usage limit. At 100%,
                    service access may be restricted until the next reset or plan upgrade.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: '20px' }}>
                  upgrade
                </span>
                <div>
                  <p className="font-semibold text-text-main-light dark:text-text-main-dark mb-1">
                    Upgrade Options
                  </p>
                  <p>
                    If you're consistently reaching your limits, consider upgrading to a higher plan
                    tier for increased usage allowances.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  )
}

